import React from 'react'
import clsx from 'clsx'
import { CalendarDays, Filter } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DASHBOARD_PRESET_LABELS,
  DASHBOARD_SELECTABLE_PRESETS,
  resolvePeriod,
  type DashboardPeriod,
  type DashboardPeriodPreset,
} from '@/lib/dashboardPeriod'
import { endOfDay, startOfDay } from 'date-fns'

const ALL_PIPELINES = '__all__'

function inputDate(date: Date) {
  const offset = date.getTimezoneOffset()
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 10)
}

function localDate(value: string) {
  return new Date(`${value}T00:00:00`)
}

interface DashboardFiltersProps {
  period: DashboardPeriod
  onPeriodChange: (period: DashboardPeriod) => void
  pipelines: { id: string; name: string }[]
  pipelineId: string | null
  onPipelineChange: (pipelineId: string | null) => void
  pipelinesLoading?: boolean
  comparisonLabel: string
}

const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  period,
  onPeriodChange,
  pipelines,
  pipelineId,
  onPipelineChange,
  pipelinesLoading = false,
  comparisonLabel,
}) => {
  const handlePreset = (preset: DashboardPeriodPreset) => {
    if (preset === 'custom') {
      onPeriodChange({ ...period, preset: 'custom' })
      return
    }
    onPeriodChange(resolvePeriod(preset))
  }

  return (
    <div className="flex flex-col gap-3 rounded-[20px] border border-border bg-card p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 pr-1 text-muted-foreground">
          <CalendarDays className="h-4 w-4" />
          <span className="text-[12px] font-medium">Período</span>
        </div>

        <div className="flex flex-wrap items-center gap-1 rounded-full bg-card-muted p-1">
          {DASHBOARD_SELECTABLE_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => handlePreset(preset)}
              className={clsx(
                'rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors',
                period.preset === preset
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {DASHBOARD_PRESET_LABELS[preset]}
            </button>
          ))}
          <button
            type="button"
            onClick={() => handlePreset('custom')}
            className={clsx(
              'rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors',
              period.preset === 'custom'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {DASHBOARD_PRESET_LABELS.custom}
          </button>
        </div>

        {period.preset === 'custom' && (
          <div className="flex items-end gap-2">
            <Input
              type="date"
              aria-label="Data inicial"
              value={inputDate(period.from)}
              max={inputDate(period.to)}
              onChange={(event) =>
                onPeriodChange({
                  preset: 'custom',
                  from: startOfDay(localDate(event.target.value)),
                  to: period.to,
                })
              }
              className="h-9 w-[144px]"
            />
            <span className="pb-2 text-[12px] text-muted-foreground">até</span>
            <Input
              type="date"
              aria-label="Data final"
              value={inputDate(period.to)}
              min={inputDate(period.from)}
              onChange={(event) =>
                onPeriodChange({
                  preset: 'custom',
                  from: period.from,
                  to: endOfDay(localDate(event.target.value)),
                })
              }
              className="h-9 w-[144px]"
            />
          </div>
        )}

        <span className="text-[11px] text-muted-foreground">{comparisonLabel}</span>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Filter className="h-4 w-4" />
          <span className="text-[12px] font-medium">Funil</span>
        </div>
        <Select
          value={pipelineId ?? ALL_PIPELINES}
          onValueChange={(value) => onPipelineChange(value === ALL_PIPELINES ? null : value)}
          disabled={pipelinesLoading}
        >
          <SelectTrigger className="h-9 w-full min-w-[200px] lg:w-[240px]">
            <SelectValue placeholder={pipelinesLoading ? 'Carregando...' : 'Todos os funis'} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={ALL_PIPELINES}>Todos os funis</SelectItem>
              {pipelines.map((pipeline) => (
                <SelectItem key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

export default DashboardFilters
